import { api, resetRefreshCircuit } from "@/lib/api-client";
import type { LoginInput } from "./schemas";
import type { LoginResponse, User, WorkspacesResponse } from "./types";

export const authApi = {
  login: async (data: LoginInput) => {
    const payload: Record<string, string> = {
      email: data.email,
      password: data.password,
    };
    if (data.mfa_code) payload.mfa_code = data.mfa_code;
    const res = await api.post<LoginResponse>("/auth/login/", payload);
    resetRefreshCircuit();
    return res.data;
  },

  logout: async () => {
    await api.post("/auth/logout/");
  },

  me: async () => {
    const res = await api.get<User>("/auth/me/");
    return res.data;
  },

  workspaces: async () => {
    const res = await api.get<WorkspacesResponse>("/auth/workspaces/");
    return res.data;
  },

  switchWorkspace: async (tenantId: string) => {
    const res = await api.post<{ user: User }>("/auth/switch-workspace/", {
      tenant_id: tenantId,
    });
    return res.data;
  },

  verifyEmail: async (token: string) => {
    const res = await api.post<{ message: string }>("/auth/verify-email/", {
      token,
    });
    return res.data;
  },

  resendVerification: async () => {
    const res = await api.post<{ message: string }>(
      "/auth/resend-verification/",
    );
    return res.data;
  },

  updateMe: async (data: {
    first_name?: string;
    last_name?: string;
    phone?: string;
  }) => {
    const res = await api.patch<User>("/auth/me/", data);
    return res.data;
  },

  notificationPrefs: async () => {
    const res = await api.get<Record<string, boolean>>(
      "/auth/notification-preferences/",
    );
    return res.data;
  },

  updateNotificationPrefs: async (data: Record<string, boolean>) => {
    const res = await api.patch<Record<string, boolean>>(
      "/auth/notification-preferences/",
      data,
    );
    return res.data;
  },

  changePassword: async (data: {
    current_password: string;
    new_password: string;
    new_password_confirm: string;
  }) => {
    await api.post("/auth/change-password/", data);
  },

  mfaSetup: async () => {
    const res = await api.post<{ secret: string; qr_code_url: string }>(
      "/auth/mfa/setup/",
    );
    return res.data;
  },

  mfaVerify: async (code: string) => {
    const res = await api.post<{ backup_codes: string[] }>(
      "/auth/mfa/verify/",
      { code },
    );
    return res.data;
  },

  mfaDisable: async (code: string) => {
    await api.post("/auth/mfa/disable/", { code });
  },

  /** Public platform config for the auth pages (no auth needed). */
  config: async () => {
    const res = await api.get<{ public_signup_enabled: boolean }>(
      "/auth/config/",
    );
    return res.data;
  },

  register: async (data: {
    company_name: string;
    email: string;
    first_name?: string;
    last_name?: string;
    password: string;
    password_confirm: string;
  }) => {
    const res = await api.post<LoginResponse>("/auth/register/", data);
    resetRefreshCircuit();
    return res.data;
  },

  passwordResetRequest: async (email: string) => {
    await api.post("/auth/password-reset/", { email });
  },

  passwordResetConfirm: async (data: {
    token: string;
    new_password: string;
    new_password_confirm: string;
  }) => {
    // The backend also sets auth cookies and returns the user, so the caller
    // can log the person straight in (used by the team-invite set-password flow).
    const res = await api.post<{ message: string; user?: User }>(
      "/auth/password-reset/confirm/",
      data,
    );
    resetRefreshCircuit();
    return res.data;
  },
};
